import { useState } from "react";
import { CAMPO, Recado } from "./ui";
import {
  definirBonus,
  mensagemDoCatalogo,
  type Catalogo,
  type ProdutoDoCatalogo,
} from "../lib/adminCatalogo";

/**
 * Admin › Catálogo › Produtos › editar um SKU › **os bônus de acesso** (000273).
 *
 * ---------------------------------------------------------------------------
 * O que um bônus é
 * ---------------------------------------------------------------------------
 * Pedido de Victor em 21/09: *"em cada produto, deve ser possível configurar os
 * Bônus de acesso — bônus de 6 meses de escola e/ou bônus de 1 ano de Pós DP.
 * Deve ser possível adicionar mais de 1"*.
 *
 * A regra já existia e já roda em toda venda (`aplicar_bonus_catalogo`); o que
 * faltava era onde mexer nela.
 *
 * ---------------------------------------------------------------------------
 * DOIS bônus com a mesma forma, e a tela diz qual é qual
 * ---------------------------------------------------------------------------
 *   · **cruzado** — comprou este produto, ganha N meses de OUTRO. O acesso
 *     nasce com `origem = 'bonus'` e empilha sobre o que o aluno já tem.
 *   · **do próprio produto** (`auto`) — estende o prazo DELE em N meses, além
 *     do que o catálogo diz. São quatro linhas assim hoje, e não são engano: é
 *     como a casa vende "12 meses + 12 de bônus" sem mexer no `prazo_meses`.
 *
 * Elas aparecem separadas de propósito. Numa lista só, "Aprovação Contábil dá
 * 12 meses de Aprovação Contábil" se lê como defeito — e quem apagasse a linha
 * cortaria pela metade o prazo de quem comprar amanhã.
 *
 * ---------------------------------------------------------------------------
 * O que esta tela NÃO faz, e diz que não faz
 * ---------------------------------------------------------------------------
 * Bônus é regra de CONCESSÃO: ele age na próxima venda. Acrescentar não devolve
 * nada a quem já comprou, e tirar não tira nada de quem já ganhou — os acessos
 * com `origem = 'bonus'` ficam onde estão. A expectativa oposta é razoável, e a
 * frase do banco a responde em todo ato.
 *
 * E nada aqui é decidido no front: o motivo obrigatório na remoção, o teto de
 * meses e o aviso do destino desativado são da RPC. Esta tela mostra a frase
 * que ela manda.
 */

/** O SKU escolhido no combo, e por quantos meses. Um formulário, um bônus. */
type Novo = { destino: string; meses: string; motivo: string };

const VAZIO: Novo = { destino: "", meses: "", motivo: "" };

export default function AdminCatalogoProdutoBonus({
  catalogo,
  produto,
  recarregar,
}: {
  catalogo: Catalogo;
  produto: ProdutoDoCatalogo;
  recarregar: () => Promise<void>;
}) {
  const [novo, setNovo] = useState<Novo>(VAZIO);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  /** O bônus que está sendo removido, com o motivo — um por vez, como a Vitrine. */
  const [tirando, setTirando] = useState<{ slug: string; motivo: string } | null>(null);

  const bonus = produto.bonus ?? [];
  const cruzados = bonus.filter((b) => !b.auto);
  const doProprio = bonus.find((b) => b.auto) ?? null;

  // O combo lista TODOS os produtos, menos os que já são bônus deste. O próprio
  // produto FICA na lista: a auto-referência é um bônus legítimo, e escondê-la
  // tiraria da tela o único jeito de criar a linha que quatro produtos já têm.
  const disponiveis = catalogo.produtos
    .filter((p) => !bonus.some((b) => b.slug === p.slug))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  async function agir(chave: string, fazer: () => ReturnType<typeof definirBonus>) {
    setSalvando(chave);
    setRecado(null);
    try {
      const r = await fazer();
      setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        setNovo(VAZIO);
        setTirando(null);
        await recarregar();
      }
    } catch (e) {
      setRecado({
        tipo: "erro",
        texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined),
      });
    } finally {
      setSalvando(null);
    }
  }

  /** Uma linha da lista, com o botão de tirar e a caixa de motivo embaixo. */
  function Linha({ b }: { b: (typeof bonus)[number] }) {
    const confirmando = tirando?.slug === b.slug;
    return (
      <li className="py-3" data-bonus={b.slug}>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-semibold text-tinta">
            {b.meses} {b.meses === 1 ? "mês" : "meses"}
          </span>
          <span className="text-sm text-cinza">
            de <strong className="font-semibold text-tinta">{b.nome}</strong>
          </span>
          {/* O destino DESATIVADO é dito na linha, e não só quando alguém
              salva: o acesso é concedido e o aluno não vê o curso, porque a
              vitrine filtra `ativo`. */}
          {!b.ativo && (
            <span className="rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-semibold text-alerta-tinta">
              produto desativado
            </span>
          )}
          <button
            type="button"
            data-tirar-bonus={b.slug}
            disabled={salvando !== null}
            onClick={() => setTirando(confirmando ? null : { slug: b.slug, motivo: "" })}
            className="ml-auto text-sm font-semibold text-link underline underline-offset-2 disabled:opacity-55"
          >
            {confirmando ? "Cancelar" : "Tirar"}
          </button>
        </div>

        {confirmando && (
          <div className="mt-3 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-3">
            <p className="text-sm text-alerta-tinta">
              As próximas vendas de «{produto.nome}» deixam de conceder isto.{" "}
              <strong>Quem já ganhou não perde nada</strong> — o acesso dele continua onde está.
            </p>
            <label className="mt-3 flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-alerta-tinta">Justifique a alteração</span>
              <input
                data-campo="motivo-bonus"
                className={CAMPO}
                value={tirando.motivo}
                onChange={(e) => setTirando({ ...tirando, motivo: e.target.value })}
                placeholder="Vai para o registro de mudanças"
              />
            </label>
            {/* Sem `disabled` por motivo vazio: quem decide se o porquê é
                obrigatório é o banco, e a recusa dele explica o que o ato faz.
                Desabilitar aqui esconderia a explicação. */}
            <button
              type="button"
              data-confirmar-tirar
              disabled={salvando !== null}
              onClick={() =>
                void agir(b.slug, () =>
                  definirBonus(produto.slug, b.slug, null, tirando.motivo.trim() || null),
                )
              }
              className="pill-cta mt-3 min-h-10 px-5 text-sm disabled:opacity-55"
            >
              {salvando === b.slug ? "Tirando…" : "Tirar o bônus"}
            </button>
          </div>
        )}
      </li>
    );
  }

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-bonus">
      <h3 id="titulo-bonus" className="font-display text-lg font-bold text-titulo">
        Bônus de acesso
      </h3>
      <p className="mt-1 max-w-3xl text-sm text-cinza">
        O que quem compra <strong className="text-tinta">{produto.nome}</strong> ganha junto. Vale
        para as PRÓXIMAS vendas — pôr ou tirar aqui não mexe no acesso de quem já comprou.
      </p>

      {/* ── O que já existe ────────────────────────────────────────────────── */}
      {cruzados.length > 0 ? (
        <ul className="mt-5 divide-y divide-borda" data-bonus-cruzados>
          {cruzados.map((b) => (
            <Linha key={b.slug} b={b} />
          ))}
        </ul>
      ) : (
        /* A ausência é RESPOSTA. "Nenhum bônus" e um espaço em branco são
           estados diferentes, e o segundo se lê como tela quebrada. */
        <p className="mt-5 text-sm text-cinza" data-sem-bonus>
          Este produto não dá nenhum curso de bônus hoje.
        </p>
      )}

      {/* ── O do próprio produto, à parte ──────────────────────────────────── */}
      {/*
        O TOTAL vem primeiro, e em negrito, porque é o número que decide —
        `prazo_meses` e o bônus são as duas parcelas, e ninguém deveria precisar
        somá-las de cabeça para saber até quando o aluno entra.
        Victor leu esta caixa em 21/09 e a somou errado; no mesmo minuto a conta
        certa mostrou que a Pós CASP concede 36 meses num curso que a casa
        descreve como de 24 — o número que faltava na tela era justamente este.
      */}
      {doProprio && (
        <div className="mt-5 rounded-cf border border-borda bg-tile p-4" data-bonus-do-proprio>
          <p className="text-sm text-cinza">
            Com este bônus, quem compra fica com{" "}
            <strong className="text-tinta" data-total-do-prazo>
              {produto.prazo_tipo === "meses" && produto.prazo_meses !== null
                ? `${produto.prazo_meses + doProprio.meses} meses de acesso no total`
                : "o prazo do catálogo mais este bônus"}
            </strong>
            {produto.prazo_tipo === "meses" && produto.prazo_meses !== null && (
              <> — {produto.prazo_meses} do catálogo e mais {doProprio.meses} de bônus</>
            )}
            .
          </p>
          <ul className="mt-2">
            <Linha b={doProprio} />
          </ul>
        </div>
      )}

      {/* ── Acrescentar ────────────────────────────────────────────────────── */}
      <div className="mt-6 border-t border-borda pt-5">
        <h4 className="text-sm font-semibold text-tinta">Acrescentar um bônus</h4>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="flex min-w-64 flex-1 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Curso de bônus</span>
            <select
              data-campo="bonus-destino"
              className={CAMPO}
              value={novo.destino}
              onChange={(e) => setNovo({ ...novo, destino: e.target.value })}
            >
              <option value="">escolha…</option>
              {disponiveis.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.nome}
                  {p.slug === produto.slug ? " (o próprio, estende o prazo)" : ""}
                  {p.ativo ? "" : " — desativado"}
                </option>
              ))}
            </select>
          </label>
          <label className="flex w-32 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Meses</span>
            <input
              data-campo="bonus-meses"
              className={CAMPO}
              inputMode="numeric"
              value={novo.meses}
              onChange={(e) =>
                setNovo({ ...novo, meses: e.target.value.replace(/\D/g, "").slice(0, 3) })
              }
              placeholder="6"
            />
          </label>
          <button
            type="button"
            data-por-bonus
            disabled={salvando !== null || !novo.destino || !novo.meses}
            onClick={() =>
              void agir("novo", () =>
                definirBonus(produto.slug, novo.destino, Number(novo.meses), novo.motivo.trim() || null),
              )
            }
            className="btn-contorno min-h-12 px-5 text-sm disabled:opacity-55"
          >
            {salvando === "novo" ? "Salvando…" : "Acrescentar"}
          </button>
        </div>
        {/* O teto e o mínimo são do BANCO, e a tela só os conta. Repeti-los como
            validação aqui criaria a segunda definição deles. */}
        <p className="mt-2 text-xs text-cinza">
          De 1 a 120 meses. Para mudar um bônus que já existe, acrescente-o de novo com o número
          novo — o banco substitui.
        </p>
      </div>

      {recado && (
        <div className="mt-4">
          <Recado tipo={recado.tipo} texto={recado.texto} />
        </div>
      )}
    </section>
  );
}
